import React, { useEffect, useReducer, useMemo, useCallback } from "react";
import AppError from "./AppError";
import AppActive from "./AppActive";
import AppLoad from "./AppLoad";
import appErrorWrapper from "./AppErrorWrapper";
import initialState from "../../state/initialState";
import reducer from "../../state/stateReducer";
import StateContext from "../../state/stateContext";
import useQueryChildData from "../Hooks/useQueryChildData";
import useQueryCommitment from "../Hooks/useQueryCommitment";
import useQueryFeeds from "../Hooks/useQueryFeeds";
import { checkActiveSponsorship } from "../../services/requestServices";
import {logToClientLogger} from "../../helpers/clientLogger";
import appSettings from "../../settings/appSettings";
import { 
  getChildIdParam, 
  parseErrorType, 
  CHILD_RCDS_ERROR_NO_GUID,
} from "../../helpers/helpers";
import { isAuth } from "../../services/requestServices";


function App(props) {
  const { urls, loginRedirectUrl } = appSettings;
  const { mywv="https://my.worldvision.org" } = urls;
  const [state, dispatch] = useReducer(reducer, initialState);
  const stateContextMemo = useMemo(() => {
      return { state, dispatch };
  }, [state, dispatch]);
  const queryChildData = useQueryChildData({state, dispatch});
  const queryCommitment = useQueryCommitment({ state, dispatch });
  const queryFeeds = useQueryFeeds({state, dispatch});
  const appStatus = state.app.status || "inactive";

  const isInactive = appStatus === "inactive";
  const isActive = appStatus === "active";
  const isError = appStatus === "error";
  const isLoading = appStatus === "loading";

  const accountUrl = `${window.location.origin}/account`;
  const mscUrl = `${window.location.origin}/sponsored-child`;
  const mscAllUrl = `${window.location.origin}/msc-all`;
  const setAppStatus = (status) =>{
    dispatch({type: "setAppStatus", payload: status});
  }

  const errorMsg = state?.app?.message || "";
  const setErrorMsg = (msg) => { 

    dispatch({type: "setAppError", payload: msg});

  };

  // if the call to RCDS to get Child Info fails, we need to handle based on logged in status and sponsorship history for donor. 
  const handleFailedChildDataError = useCallback(async (err) => {
    const errorType = parseErrorType(err);
    const isLoggedIn = await isAuth();

    if (!isLoggedIn && errorType === CHILD_RCDS_ERROR_NO_GUID) {
      // Set redurect to login if no guid and not authenticated.
      logToClientLogger(`Unauth and no child guid ${getChildIdParam()} force to redirect`, 'notice');
      return window.location.replace(loginRedirectUrl);
    } else if (!isLoggedIn) {
      // Set redirect to login page if bad guid and not authenticated. 
      logToClientLogger(`Unauth and bad child guid '${getChildIdParam()}' force to redirect`, 'warning');
      return window.location.replace(loginRedirectUrl);
    }

    const result = await checkActiveSponsorship();

    if (!getChildIdParam() && !result) {
      // Set redirect to login page if no guid and no result. 
      logToClientLogger(`No child guid '${getChildIdParam()}' force to redirect to account page`, 'warning');
      return window.location.replace(`${mywv}/account`);
    } else if (!result) {
      // Set Error
      logToClientLogger("Sorry, that child was not found", 'error');
      setErrorMsg("Sorry, that child was not found");
      return;
    }


    // Evaluate the result from checking active sponsorship
    const isSponsor = result.activeCount >= 1;
    const hasInactiveChildren = result.inactiveCount >= 1;
    const isSingleSponsor = isSponsor && result.activeCount === 1;

    const EventChosenRevealed = isSponsor && result.EventChosenRevealed === false && result.isChosen;

    const childGuid = !!result.childGuid && result.childGuid;
    
    if ((isSingleSponsor && EventChosenRevealed) || hasInactiveChildren) {
      // if EventChosenRevealed is false for the sponsorship, redirect back to MSC to not ruin donor reveal.  Similarly, if the account has ANY inactive sponsorships, we want to send the donor to msc-all
      return window.location.replace(mscAllUrl);
    }
    else if (isSingleSponsor && childGuid) {
      // Has a Single Sponsorship
      const hasValidGuid = await queryChildData.request(childGuid).then(() => true).catch(() => false);
      if(hasValidGuid) {
       window.history.replaceState({}, '', `${window.location.pathname}?ID=${childGuid}`);
       //we need to request the queryCommitment data once again since using .replaceState wont cause it to requery automatically.
       queryCommitment
          .request(childGuid).then(() => {  
            setAppStatus("active");          
          })
          .catch((err) => {
            console.log("err", err);
            logToClientLogger(err, 'error');
          });

        // Feeds Request
        queryFeeds.request(childGuid)
          .catch(err => {
            /* Error Already Logged - Not critical so app will not be placed in error set */
            return null;
        });
    
        setAppStatus("active");
      } else if(!hasValidGuid){
        logToClientLogger("Sorry, that child was not found", 'error');
        setErrorMsg("Sorry, that child was not found");
      }
    } else if (!isSponsor) {
      // Redirect To My Account
      return window.location.replace(accountUrl);
    } else {
      // Redirect To MSC All
      return window.location.replace(mscAllUrl);
    }
  }, [setAppStatus]);

  async function init() {
    const childId = getChildIdParam();
    if(!childId){
      setAppStatus("loading");
      return handleFailedChildDataError("No ID provided")
    }

    setAppStatus("active");

    // Request RCDS Child Data
    queryChildData
      .request(childId)
      .then((childData) => {
        // Successful Request of Child Data
      })
      .catch((err) => {
        logToClientLogger(err, 'error');
        handleFailedChildDataError(err);
      });

    // Netsuite Get Commitment By ChildGuid
    // Parallel Call Here
    queryCommitment
      .request(childId)
      .then(() => {
        setAppStatus("active");
      })
      .catch((err) => {
        logToClientLogger(err, 'error');
      });
    
    // Feeds Request
    queryFeeds.request(childId)
      .catch(err => {
        /* Error Already Logged - Not critical so app will not be placed in error set */
        return null;
      });
  }

  useEffect(() =>  {
    init();

    return () => {
      // Clean Up Logic
    };
  }, []);

  // Render App
  // data and app
  return (
    <StateContext.Provider value={stateContextMemo}>
      { 
        isActive 
          ? <AppActive 
              setAppStatus={setAppStatus}       
            />
          : null
      }
      { 
        isError 
          ? <AppError 
              message={errorMsg}
              setAppStatus={setAppStatus}     
            />
          : null
      }
      { 
        isLoading 
          ? <AppLoad   
              setAppStatus={setAppStatus}  
            />
          : null
      }
    </StateContext.Provider>
  );
}

export default appErrorWrapper(App);